import { isEmpty } from '@ember/utils';
import { conformJurisdictionalId } from 'linced-app/utils/fields';
import { validateCoreDetails } from 'linced-app/utils/nz-moe/common';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import moment from 'moment';

export const ATTENDANCE_INVALID =
  'Has not been marked as Present or Justified Absent since start date';

export function validateArrival(person, enrolment, attendance, schoolType) {
  let errors = validateCoreDetails(person, enrolment, schoolType);

  let attendanceValid = !isEmpty(attendance) && !!attendance.isValid;
  if (!attendanceValid) {
    errors.push(ATTENDANCE_INVALID);
  }

  let eligibilityCriteria = person.get(
    'fields.nzMOE:eligibilityCriteria.value',
  );
  if (isEmpty(eligibilityCriteria)) {
    errors.push('Eligibility Criteria is not set');
  }

  let fte = person.get('fields.nzMOE:fte.value');
  if (isEmpty(fte)) {
    errors.push('FTE is not set');
  } else if (fte < 0.0 || fte > 1.0) {
    errors.push('FTE is not valid');
  }

  let verificationDocument = person.get(
    'fields.nzMOE:verificationDocument.value',
  );
  let verificationSerialNumber = person.get(
    'fields.nzMOE:verificationSerialNumber.value',
  );
  if (!isEmpty(verificationDocument)) {
    // If Verification Document Type is specified, serial number must be specified.
    if (isEmpty(verificationSerialNumber)) {
      errors.push(
        'Verification Document Serial Number is required if a Verification Document type is specified.',
      );
    }
    // If Verification Document Type is one of the following, expiry date must be specified and valid.
    if (['61023', '61024', '61031', '61032'].includes(verificationDocument)) {
      let verificationExpiryDate = person.get(
        'fields.nzMOE:verificationExpiryDate.value',
      );
      if (isEmpty(verificationExpiryDate)) {
        errors.push(
          'Verification Document Expiry Date is required for the specified Verification Document type.',
        );
      } else {
        let verificationMoment = moment(verificationExpiryDate, 'YYYY-MM-DD');
        let minExpiry = moment().subtract(1, 'years');
        let maxExpiry = moment().add(50, 'years');
        if (!verificationMoment.isValid()) {
          errors.push('Verification Document Expiry Date is invalid.');
        } else if (
          verificationMoment.isBefore(minExpiry, 'day') ||
          verificationMoment.isAfter(maxExpiry, 'day')
        ) {
          errors.push(
            'Verification Document Expiry Date must be no more than 1 year in the past or 50 years in the future.',
          );
        }
      }
    }
  } else {
    // If no Verification Document Type is specified, there must not be a serial number.
    if (!isEmpty(verificationSerialNumber)) {
      errors.push(
        'Verification Document Serial Number must not be specified if no Verification Document type is specified.',
      );
    }
  }

  let exchangeScheme = person.get('fields.nzMOE:exchangeScheme.value');
  if (eligibilityCriteria === '60005') {
    // Student on Government approved exchange scheme; Exchange Scheme is required.
    if (isEmpty(exchangeScheme)) {
      errors.push('Exchange Scheme is required for exchange students.');
    }
  } else if (!isEmpty(exchangeScheme)) {
    // Not an exchange student - must not specify an exchange scheme.
    errors.push(
      'Exchange Scheme must not be specified except for exchange students.',
    );
  }

  let citizenship = person.get('fields.nzMOE:countryCitizenship.value');
  if (!isEmpty(verificationDocument) && isEmpty(citizenship)) {
    // Citizenship is required if a Verification Document Type is specified.
    errors.push('Citizenship is required.');
    // Technically, it must _not_ be present if no Verification Document Type is specified,
    // but we just skip sending it in the Arrival Upload.
  }

  if (eligibilityCriteria === '60004') {
    // For international fee-paying students, the tuition fee must be greater than $0.
    if ((person.get('fields.nzMOE:fee.value') ?? 0) <= 0) {
      errors.push('Weekly tuition fee must be greater than $0.00');
    }
  }

  return errors;
}

export function generateArrivalRequestParams(person, enrolment, session) {
  let fields = person.get('fields');
  let nsn = conformJurisdictionalId(
    Jurisdiction.NZ_MOE,
    person.jurisdictionalId,
  );
  let yearLevel = person.get('currentYearLevel') ?? 0;
  if (yearLevel === 0) {
    // We should only be generating params for already-validated learners,
    // so if we get a Year 0, that should be allowed for this school.
    // Turn them into a Year 1 for the upload, as ENROL doesn't allow Year 0.
    yearLevel = 1;
  }
  return {
    // Request params.
    requesterUsername: session.user.username,
    providerId: session.school.jurisdictionalId,
    personId: person.id,
    personEnrolmentId: enrolment.id,

    // Core identity data.
    nsn,
    birthDate: fields.get('core:dob.value'),
    gender: fields.get('nzMOE:gender.value'),

    // Required arrival data.
    startDate: enrolment.entryDate,
    yearLevel,
    eligibilityStatus: fields.get('nzMOE:eligibilityCriteria.value'),
    // FTE is sent as an integer percentage; back end converts it back and formats to 1dp.
    fte: (fields.get('nzMOE:fte.value') ?? 0) * 100,

    // Optional arrival data.
    verificationDocumentType: fields.get('nzMOE:verificationDocument.value'),
    verificationDocumentId: fields.get('nzMOE:verificationSerialNumber.value'),
    verificationDocumentExpiryDate: fields.get(
      'nzMOE:verificationExpiryDate.value',
    ),
    exchangeScheme: fields.get('nzMOE:exchangeScheme.value'),
    citizenship: fields.get('nzMOE:countryCitizenship.value'),
    ethnicities: fields.get('nzMOE:ethnicity.value').slice(),
    iwis: fields.get('nzMOE:iwi.value').slice(),
    address1: fields.get('nzMOE:address1.value'),
    address2: fields.get('nzMOE:address2.value'),
    address3: fields.get('nzMOE:address3.value'),
    postCode: fields.get('nzMOE:address4.value'),
    tuitionFee: fields.get('nzMOE:fee.value'),
  };
}
